var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { IsArray, IsDateString, IsInt, IsOptional, IsString, Min, ValidateNested, } from 'class-validator';
/**
 * One prescribed Heilmittel, identified by its X-Positionsnummer — the direct
 * join key to the catalog's allowed options (no mapping table, see design memo
 * `gkv-prescription-validation-design`). `name` carries the display label so
 * findings can name the Heilmittel without a second lookup.
 *
 * `posNumber: null` says the Heilmittel has **no** position in the Verordnung's
 * Diagnosegruppe — a `KG` on an `LY` Verordnung. That is the answer, not a gap in
 * the input: the Heilmittel is not in the group, and it is judged as such. A
 * Heilmittel that cannot be resolved for another reason is left out instead.
 */
export class PrescribedHeilmittelInput {
    posNumber;
    name;
    /**
     * Units prescribed for this Heilmittel. The Höchstmenge rule takes ergänzende
     * and not-in-group units back out of `plannedUnits` by it; absent ⇒ nothing
     * is taken out.
     */
    units;
}
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], PrescribedHeilmittelInput.prototype, "posNumber", void 0);
__decorate([
    Expose(),
    IsString(),
    __metadata("design:type", String)
], PrescribedHeilmittelInput.prototype, "name", void 0);
__decorate([
    Expose(),
    IsInt(),
    Min(0),
    IsOptional(),
    __metadata("design:type", Number)
], PrescribedHeilmittelInput.prototype, "units", void 0);
/**
 * The live prescription snapshot the capture form submits for validation
 * (debounced) and the Setup-lane task re-runs on read. All fields are optional
 * so a half-filled form still validates what it can. The catalog release is
 * resolved by `prescriptionDate` (Ausstellungsdatum — Bestandsschutz, ADR-0026);
 * an absent date falls back to today.
 */
export class ValidatePrescriptionRequestDto {
    /** Ausstellungsdatum, ISO date (YYYY-MM-DD). Resolves the catalog release. */
    prescriptionDate;
    /** diagnosegruppe@V, e.g. `CS`. Unknown codes fire the group-exists rule. */
    diagnosisGroupCode;
    /** The prescribed Heilmittel, by X-Positionsnummer. */
    heilmittel;
    /**
     * Total planned treatment units on the Verordnung — the whole-prescription sum,
     * including rows not listed in `heilmittel`. The Höchstmenge rule judges it,
     * less the ergänzende and not-in-group units, against the Diagnosegruppe's
     * Höchstmenge je Verordnung. Absent while the form is still empty ⇒ the
     * Höchstmenge rule abstains.
     */
    plannedUnits;
    /**
     * The prescribed weekly frequency span (einheiten_pro_woche), judged against
     * the group's Frequenzempfehlung. Sub-weekly cadences (alle N Wochen) and an
     * uncaptured frequency arrive as null ⇒ the frequency rule abstains.
     */
    weeklyFrequencyMin;
    weeklyFrequencyMax;
    /**
     * The captured primary ICD-10 code (faithful free-text snapshot, TM-9126).
     * Feeds the ICD gate and ICD-format rules. Absent ⇒ those rules abstain.
     */
    icd10Code;
    /**
     * The captured secondary ICD-10 code — Muster 13 carries two, and SDHM
     * erforderliche_diagnosen gates frequently hit the secondary. An ICD gate is
     * satisfied when EITHER captured code matches.
     */
    icd10Code2;
    /** Captured Leitsymptomatik codes (a/b/c), for the required-Leitsymptomatik gate. */
    leitsymptomatikCodes;
    /** Patientenindividuelle Leitsymptomatik free text — the escape from the a/b/c gate. */
    leitsymptomatikText;
    /**
     * The patient's date of birth (ISO date, YYYY-MM-DD). The handler derives the
     * age at the Ausstellungsdatum for the age-bounds rule; absent ⇒ that rule
     * abstains visibly.
     */
    patientDateOfBirth;
}
__decorate([
    Expose(),
    IsDateString(),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "prescriptionDate", void 0);
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "diagnosisGroupCode", void 0);
__decorate([
    Expose(),
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => PrescribedHeilmittelInput),
    __metadata("design:type", Array)
], ValidatePrescriptionRequestDto.prototype, "heilmittel", void 0);
__decorate([
    Expose(),
    IsInt(),
    Min(0),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "plannedUnits", void 0);
__decorate([
    Expose(),
    IsInt(),
    Min(0),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "weeklyFrequencyMin", void 0);
__decorate([
    Expose(),
    IsInt(),
    Min(0),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "weeklyFrequencyMax", void 0);
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "icd10Code", void 0);
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "icd10Code2", void 0);
__decorate([
    Expose(),
    IsArray(),
    IsString({ each: true }),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "leitsymptomatikCodes", void 0);
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "leitsymptomatikText", void 0);
__decorate([
    Expose(),
    IsDateString(),
    IsOptional(),
    __metadata("design:type", Object)
], ValidatePrescriptionRequestDto.prototype, "patientDateOfBirth", void 0);
/** The advisory output for a prescription snapshot: catalog deviations plus the
 * informational BVB/LHM qualifications that apply to the captured diagnosis. */
export class ValidatePrescriptionResultDto {
    findings;
    /**
     * BVB/LHM badges (RS-009) — the besonderer Verordnungsbedarf / langfristiger
     * Heilmittelbedarf entries the captured ICDs qualify for at the
     * Ausstellungsdatum. Informational only; never a finding, never counted as a
     * Katalog-Abweichung.
     */
    prescriptionNeeds;
}
//# sourceMappingURL=validate-prescription.dto.js.map